import React, { useCallback, useEffect, useState } from 'react';
import { authedFetch } from './utils/api';
import { color, naira, radius, space } from './ui/theme';
import { Banner, Btn } from './ui/kit';

/**
 * Asked before a receipt is produced: has the customer actually paid?
 *
 * The three answers lead to different documents, because the document has to
 * match what happened:
 *
 *   Paid in full   money recorded for the whole balance, then a RECEIPT
 *   Part payment   money recorded for what was handed over, then an INVOICE
 *                  showing what was paid and what is still due
 *   Not yet        nothing recorded, an INVOICE for the full amount
 *
 * A receipt is only ever produced where the balance reaches zero. That is the
 * rule that was missing: thirty orders carry a receipt saying PAID with no
 * money recorded against them, because the receipt was the first step rather
 * than the last.
 */

const inputStyle = {
  width: '100%', padding: '9px 11px', borderRadius: radius.sm,
  border: `1px solid ${color.border}`, fontSize: 13.5,
};

async function req(url, opts) {
  const res = await authedFetch(url, opts);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
  return body;
}

export default function TakePaymentDialog({
  orderId, orderNumber, onClose, onReceipt, onInvoice, notify,
}) {
  const [state, setState] = useState(null);
  const [choice, setChoice] = useState('full');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const s = await req(`/api/payment-reconciliation/order/${orderId}`);
      setState(s);
      setAmount(String(s.outstanding));
    } catch (e) { setError(e.message); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const go = async () => {
    if (choice === 'none') { onInvoice?.(); onClose?.(); return; }

    const value = choice === 'full' ? null : Number(amount);
    if (choice === 'part') {
      if (!(value > 0)) { notify?.('Enter how much was received', 'error'); return; }
      if (value >= state.outstanding) {
        notify?.('That is the whole balance — choose "Paid in full"', 'error');
        return;
      }
    }

    setBusy(true);
    try {
      const paid = await req(
        `/api/payment-reconciliation/order/${orderId}/take-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: value,
            payment_method: method,
            reference: reference.trim() || null,
          }),
        });

      if (paid.is_part_payment) {
        // Money recorded, but the order is not settled, so the customer gets
        // an invoice showing what is still owed -- not a receipt implying
        // the account is clear.
        notify?.(
          `${naira(paid.amount_received)} recorded. `
          + `${naira(paid.balance_outstanding)} still outstanding.`,
          'warning');
        onInvoice?.();
      } else {
        notify?.(`Receipt ${paid.receipt_number}. Paid in full.`, 'success');
        onReceipt?.();
      }
      onClose?.();
    } catch (e) { notify?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520, width: '94%' }}
           onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Has the customer paid?</h3>
          <Btn size="sm" variant="secondary" onClick={onClose}>Cancel</Btn>
        </div>

        <div className="modal-body">
          {error && <Banner tone="danger" title="Could not load">{error}</Banner>}

          {state && (
            <>
              <div style={{ fontSize: 13, marginBottom: space(1.5) }}>
                <strong>{state.customer}</strong> &middot; {orderNumber}
                <div style={{ color: color.textSecondary, marginTop: 4 }}>
                  Order {naira(state.total)}
                  {state.paid > 0 && ` · already paid ${naira(state.paid)}`}
                  {' '}&middot; <strong>outstanding {naira(state.outstanding)}</strong>
                </div>
              </div>

              {state.flag_disagrees && (
                <Banner tone="danger" title="This order is flagged paid with no money recorded">
                  Resolve it under Payment Reconciliation before taking more.
                </Banner>
              )}

              <div style={{ display: 'grid', gap: 8, marginBottom: space(1.5) }}>
                {[
                  ['full', 'Paid in full', `Record ${naira(state.outstanding)} and produce a receipt`],
                  ['part', 'Part payment', 'Record what was handed over; an invoice shows the rest'],
                  ['none', 'Not yet', 'Nothing recorded; invoice for the full amount'],
                ].map(([v, label, hint]) => (
                  <label key={v}
                         style={{ display: 'flex', gap: 10, padding: '10px 12px',
                                  border: `1px solid ${choice === v ? color.info : color.border}`,
                                  background: choice === v ? color.infoBg : 'transparent',
                                  borderRadius: radius.sm, cursor: 'pointer' }}>
                    <input type="radio" name="paid" value={v}
                           checked={choice === v}
                           onChange={() => setChoice(v)} />
                    <span>
                      <span style={{ fontWeight: 600, fontSize: 13.5 }}>{label}</span>
                      <span style={{ display: 'block', fontSize: 12,
                                     color: color.textSecondary }}>{hint}</span>
                    </span>
                  </label>
                ))}
              </div>

              {choice === 'part' && (
                <div style={{ marginBottom: space(1) }}>
                  <div style={{ fontSize: 11.5, color: color.textSecondary,
                                marginBottom: 4 }}>
                    Amount received
                  </div>
                  <input style={inputStyle} inputMode="decimal" value={amount}
                         autoFocus
                         onChange={(e) => setAmount(e.target.value)} />
                </div>
              )}

              {choice !== 'none' && (
                <div style={{ display: 'grid', gap: 8,
                              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                  <label>
                    <div style={{ fontSize: 11.5, color: color.textSecondary,
                                  marginBottom: 4 }}>How was it paid</div>
                    <select style={inputStyle} value={method}
                            onChange={(e) => setMethod(e.target.value)}>
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank transfer</option>
                      <option value="pos">POS</option>
                      <option value="cheque">Cheque</option>
                    </select>
                  </label>
                  <label>
                    <div style={{ fontSize: 11.5, color: color.textSecondary,
                                  marginBottom: 4 }}>Reference</div>
                    <input style={inputStyle} value={reference}
                           placeholder="Teller or transfer reference"
                           onChange={(e) => setReference(e.target.value)} />
                  </label>
                </div>
              )}

              <div style={{ marginTop: space(2) }}>
                <Btn disabled={busy || state.outstanding <= 0} onClick={go}>
                  {busy ? 'Recording…'
                    : choice === 'full' ? 'Record payment and print receipt'
                    : choice === 'part' ? 'Record part payment and print invoice'
                    : 'Print invoice'}
                </Btn>
              </div>

              {state.outstanding <= 0 && (
                <Banner tone="success" title="Already settled">
                  Nothing is outstanding on this order.
                </Banner>
              )}

              <div style={{ fontSize: 11, color: color.textMuted,
                            marginTop: space(1.5) }}>
                A receipt is only produced when the balance reaches zero. A
                part payment gets an invoice showing what was paid and what
                is still owed, so it can be chased.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
